import { WindowLocation } from "@reach/router";
import { graphql } from "gatsby";
import React from "react";
import BlockContent from "../components/BlockContent/BlockContent";
import BlockContentOutline from "../components/BlockContentOutline/BlockContentOutline";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs";
import Card from "../components/Card/Card";
import ContentFooter from "../components/ContentFooter/ContentFooter";
import DataEmbed from "../components/DataEmbed/DataEmbed";
import EditorEmbed from "../components/EditorEmbed/EditorEmbed";
import EditorEmbedSupport from "../components/EditorEmbedSupport/EditorEmbedSupport";
import LevelIndicator from "../components/LevelIndicator/LevelIndicator";
import TagInfo from "../components/TagInfo/TagInfo";
import { FormattedMessage } from "react-intl";
import ContentLayout from "../layouts/ContentLayout/ContentLayout";
import PageLayout from "../layouts/PageLayout/PageLayout";
import { SanityAccessibilityActivity, SanityAccessibilityConfig } from "../model/accessibility";
import {
  CommonContext,
  MicrobitCode,
  SanityCampaign,
  SanityPage
} from "../model/common";
import { GlobalMenus } from "../model/menu";
import { urlForDocument } from "../urls";
import { SiteArea } from "../utils/analytics";
import { getBreadcrumbs } from "../utils/site-hierarchy";

export const pageQuery = graphql`
  query AccessibilityActivityPage($_id: String!, $navLanguage: String) {
    accessibilityLandingPage: sanityAccessibilityLandingPage {
      _id
      _type
      title
    }
    config: sanityAccessibilityConfig {
      ...AccessibilityConfig
    }
    menus: sanityGlobalConfig(language: { eq: $navLanguage }) {
      ...MenuData
    }
    page: sanityAccessibilityActivity(_id: { eq: $_id }) {
      _id
      _type
      language
      title
      fields {
        editorLinks
      }
      slug {
        current
      }
      metaDescription
      socialTitle
      socialDescription
      socialImage {
        ...SanityImage
      }
      level
      projectCode {
        _key
        name
        editors
        scratch {
          url
          file {
            asset {
              originalFilename
              extension
              path
            }
          }
          scratchImages {
            asset {
              ...SanityImageAsset
            }
          }
        }
        makecode {
          project
          url
        }
        python {
          main
        }
      }
      _rawContent(resolveReferences: { maxDepth: 8 })
      translationSponsors {
        ...TranslationSponsors
      }
      licence {
        ...Licence
      }
    }
    campaigns: allSanityCampaign(
      filter: { activeSiteAreas: { in: "accessibility" } }
    ) {
      nodes {
        ...Campaign
      }
    }
  }
`;

const AccessibilityActivityPage = ({
  pageContext,
  data: { menus, config, page, campaigns, accessibilityLandingPage },
  location
}: AccessibilityActivityPageProps) => {
  const {
    title,
    level,
    _rawContent,
    projectCode,
    licence,
    translationSponsors
  } = page;
  const breadcrumbs = getBreadcrumbs(config._rawSiteHierarchy, page, {
    parentBreadcrumbs: [
      {
        title: accessibilityLandingPage.title,
        href: urlForDocument(accessibilityLandingPage)
      }
    ],
    maxLevels: 2
  });
  const customEmbeds = {
    microbitCode: ({ node }: { node: MicrobitCode }) => (
      <EditorEmbed unit={undefined} code={node} context={page} />
    ),
    microbitCodeEmbed: ({ node }: { node: { microbitCodeRef: string } }) => {
      const code = projectCode.find(v => v._key === node.microbitCodeRef);
      if (!code) {
        return null;
      }
      return <EditorEmbed unit={undefined} code={code} context={page} />;
    },
    microbitCreateAiDataEmbed: ({
      node
    }: {
      node: { microbitCodeRef: string };
    }) => {
      const code = projectCode.find(v => v._key === node.microbitCodeRef);
      if (!code) {
        return null;
      }
      return <DataEmbed unit={undefined} code={code} context={page} />;
    }
  };

  return (
    <EditorEmbedSupport document={page}>
      <PageLayout
        siteArea={SiteArea.ACCESSIBILITY}
        metadata={{
          title,
          alternates: pageContext.alternates,
          page
        }}
        menus={menus}
        messages={pageContext.messages}
        location={location}
      >
        <ContentLayout
          title={title}
          tags={
            <TagInfo value={[[<LevelIndicator key={level} value={level} />]]} />
          }
          type={
            <span>
              <FormattedMessage id="activity" />
            </span>
          }
          breadcrumbs={<Breadcrumbs breadcrumbs={breadcrumbs} />}
          aside={
            <nav>
              <BlockContentOutline showOnMobile content={_rawContent} />
            </nav>
          }
          content={
            <>
              <Card type="main">
                <BlockContent
                  content={_rawContent}
                  customEmbeds={customEmbeds}
                  cap="3/4"
                />
                <ContentFooter
                  licence={licence}
                  translationSponsors={translationSponsors}
                />
              </Card>
            </>
          }
          campaigns={campaigns.nodes}
        />
      </PageLayout>
    </EditorEmbedSupport>
  );
};

export default AccessibilityActivityPage;

interface AccessibilityActivityPageProps {
  data: {
    menus: GlobalMenus;
    listings: SanityPage;
    page: SanityAccessibilityActivity;
    config: SanityAccessibilityConfig;
    campaigns: { nodes: SanityCampaign[] };
    accessibilityLandingPage: SanityPage;
  };
  location: WindowLocation;
  pageContext: CommonContext;
}
